<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue'
import IconComponent from '../common/IconComponent.vue'
import NotificationDrawer from './NotificationDrawer.vue'
import { useNotificationsStore } from '../../../stores/notifications'

/**
 * Header bell.
 *
 *  - Reads `unreadCount` from the notifications store so the badge stays in
 *    sync with the inbox.
 *  - Bootstraps a single initial fetch on mount + a 30s polling loop. The
 *    store guards against duplicate intervals so multiple mounts (e.g. POS
 *    + back-office headers on the same screen) don't multiply the load.
 *  - Toggles the side drawer for a quick recent-notifications glance.
 */

const store = useNotificationsStore()

const isDrawerOpen = computed({
  get: () => drawerOpen.value,
  set: (v: boolean) => (drawerOpen.value = v),
})

import { ref } from 'vue'
const drawerOpen = ref(false)

const hasNotifications = computed(() => store.unreadCount > 0)
const badgeText = computed(() => {
  const n = store.unreadCount
  if (n <= 0) return ''
  return n > 99 ? '99+' : String(n)
})

const toggleDrawer = async () => {
  drawerOpen.value = !drawerOpen.value
  if (drawerOpen.value && !store.initialLoaded) {
    await store.load()
  }
}

onMounted(async () => {
  if (!store.initialLoaded) {
    await store.load()
  }
  store.startPolling(30_000)
})

onBeforeUnmount(() => {
  // Keep polling alive even if this header instance unmounts — the store's
  // interval is a singleton. We only stop on explicit logout (auth store
  // calls reset()).
})
</script>

<template>
  <div>
    <button
      @click="toggleDrawer"
      class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative"
      title="Notifications"
      :aria-label="hasNotifications ? `Notifications (${badgeText} unread)` : 'Notifications'"
    >
      <IconComponent name="BellRing" />
      <span
        v-if="hasNotifications"
        class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow"
      >
        {{ badgeText }}
      </span>
    </button>

    <NotificationDrawer
      :is-open="isDrawerOpen"
      @close="isDrawerOpen = false"
    />
  </div>
</template>
